import React, { useState } from "react";
import { BookOpen, Clock, User, Tag, ArrowRight } from "lucide-react";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import SearchBar from "../../components/discover/SearchBar";
import { motion } from "framer-motion";

const BLOG_ARTICLES = [
  {
    id: 1,
    title: "How to Build a Minimum Viable Product (MVP) in 2026: An Actionable Guide",
    description: "Developing an MVP doesn't need to take 6 months. In this article, we outline the exact step-by-step frameworks that modern startups use to scope, design, and deploy full-stack MVPs in under 4 weeks using AI assistance, browser-based sandboxes, and visual builders.",
    excerpt: "Scoping, designing, and launching high-fidelity apps in under 4 weeks.",
    category: "Product & Engineering",
    author: "Prasannakumar V.",
    avatar: "https://images.pexels.com/photos/29852895/pexels-photo-29852895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=100",
    readingTime: "8 min read",
    date: "July 05, 2026",
    tags: ["MVP", "No-Code", "AI Tools"],
    featured: true,
    image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxzdGFydHVwJTIwdGVhbSUyMG1vZGVybiUyMG9mZmljZSUyMG1pbmltYWx8ZW58MHx8fHwxNzgyNjMyMTAxfDA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 2,
    title: "The Ultimate Guide to Pitch Deck Structure for Pre-Seed Rounds",
    description: "What are venture capital firms actually looking for in early-stage decks? We dissect the 10 critical slides you need, from problem statements to TAM size, to capture interest and secure initial investments.",
    category: "Fundraising",
    author: "Ananya Verma",
    avatar: "https://images.pexels.com/photos/33646670/pexels-photo-33646670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=100",
    readingTime: "5 min read",
    date: "July 02, 2026",
    tags: ["Pitch Deck", "VC", "Pre-Seed"],
    featured: false,
    image: "https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxzdGFydHVwJTIwdGVhbSUyMG1vZGVybiUyMG9mZmljZSUyMG1pbmltYWx8ZW58MHx8fHwxNzgyNjMyMTAxfDA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 3,
    title: "10 Growth Hacking Tactics to Acquire Your First 1,000 Customers",
    description: "Zero budget marketing strategies that scale. Learn how to leverage community platforms, viral loops, and content engine models to build awareness without paying a single dollar for paid advertisements.",
    category: "Growth & Marketing",
    author: "Rohan Sharma",
    avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=300&q=80",
    readingTime: "6 min read",
    date: "June 28, 2026",
    tags: ["Growth Hacking", "SEO", "D2C"],
    featured: false,
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxzdGFydHVwJTIwdGVhbSUyMG1vZGVybiUyMG9mZmljZSUyMG1pbmltYWx8ZW58MHx8fHwxNzgyNjMyMTAxfDA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: 4,
    title: "How to Build a Remote-First Company Culture That Succeeds",
    description: "Transitioning to a remote setup is hard, but scaling globally depends on it. Discover productivity practices, documentation tools, and asynchronous team alignments that high-performance tech companies implement daily.",
    category: "Operations",
    author: "Priya Raman",
    avatar: "https://images.pexels.com/photos/33646670/pexels-photo-33646670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=100&w=100",
    readingTime: "4 min read",
    date: "June 24, 2026",
    tags: ["Remote Work", "Culture", "HR"],
    featured: false,
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85"
  }
];

export default function StartupBlogPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = ["All", "Product & Engineering", "Fundraising", "Growth & Marketing", "Operations"];

  const filtered = BLOG_ARTICLES.filter((article) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      article.title.toLowerCase().includes(q) ||
      article.description.toLowerCase().includes(q) ||
      article.tags.some((tag) => tag.toLowerCase().includes(q));

    const matchesCategory =
      selectedCategory === "All" || article.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const featuredPost = filtered.find((p) => p.featured) || filtered[0];
  const otherPosts = filtered.filter((p) => p.id !== (featuredPost?.id || -1));

  return (
    <DiscoverLayout
      title="Startup Blog"
      subtitle="Insights, playbooks, scaling playbooks and fundraising guides curated from successful Indian founders and investors."
    >
      <div className="space-y-8">
        {/* Search and Category navigation */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-gray-150 shadow-sm">
          <div className="w-full md:w-80">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search blog articles..."
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto shrink-0 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition ${
                  selectedCategory === cat
                    ? "bg-[#7C3AED] text-white shadow-sm"
                    : "bg-gray-50 border border-gray-150 hover:bg-gray-100 text-gray-700"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
            <p className="text-sm text-gray-500 font-medium font-display">No articles match your criteria.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {/* Featured Post */}
            {selectedCategory === "All" && searchQuery === "" && featuredPost && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-[24px] border border-gray-150 shadow-sm overflow-hidden flex flex-col lg:flex-row hover:shadow-md transition duration-350 group cursor-pointer"
              >
                <div className="lg:w-1/2 overflow-hidden h-60 lg:h-auto min-h-[260px] relative">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover group-hover:scale-102 transition duration-500"
                  />
                  <span className="absolute top-4 left-4 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-sm">
                    {featuredPost.category}
                  </span>
                </div>
                <div className="lg:w-1/2 p-6 md:p-8 flex flex-col justify-between space-y-6">
                  <div className="space-y-4">
                    <h2 className="font-display font-extrabold text-gray-950 text-xl md:text-2xl leading-tight group-hover:text-[#7C3AED] transition">
                      {featuredPost.title}
                    </h2>
                    <p className="text-xs text-gray-500 leading-relaxed font-normal">
                      {featuredPost.description}
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-3">
                      <img
                        src={featuredPost.avatar}
                        alt={featuredPost.author}
                        className="w-9 h-9 rounded-full object-cover border border-gray-100"
                      />
                      <div>
                        <p className="text-xs font-bold text-gray-900">{featuredPost.author}</p>
                        <p className="text-[10px] text-gray-400 font-medium">{featuredPost.date}</p>
                      </div>
                    </div>

                    <span className="text-xs font-bold text-[#7C3AED] inline-flex items-center gap-1">
                      Read Article <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Other Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(selectedCategory === "All" && searchQuery === "" ? otherPosts : filtered).map((post) => (
                <motion.div
                  key={post.id}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-[24px] border border-gray-150 shadow-sm overflow-hidden flex flex-col justify-between hover:shadow-md transition duration-300 group cursor-pointer"
                >
                  <div>
                    <div className="h-44 overflow-hidden relative">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-102 transition duration-500"
                      />
                      <span className="absolute top-3 left-3 bg-[#7C3AED] text-white text-[9px] font-bold px-2 py-0.5 rounded">
                        {post.category}
                      </span>
                    </div>

                    <div className="p-5 space-y-3.5">
                      <div className="flex items-center gap-3 text-[10px] text-gray-400">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readingTime}</span>
                        <span>•</span>
                        <span>{post.date}</span>
                      </div>

                      <h3 className="font-display font-bold text-gray-900 text-sm leading-snug group-hover:text-[#7C3AED] transition line-clamp-2">
                        {post.title}
                      </h3>
                    </div>
                  </div>

                  <div className="px-5 pb-5 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img
                        src={post.avatar}
                        alt={post.author}
                        className="w-6 h-6 rounded-full object-cover border border-gray-100"
                      />
                      <span className="text-[10px] font-bold text-gray-700">{post.author}</span>
                    </div>

                    <div className="flex gap-1">
                      {post.tags.slice(0, 1).map((t) => (
                        <span key={t} className="text-[9px] font-semibold bg-gray-50 text-gray-400 px-2 py-0.5 rounded">
                          #{t}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DiscoverLayout>
  );
}
